import React from 'react';
import { motion } from 'framer-motion';
import { IndianRupee, Clock, UserX } from 'lucide-react';

const Problem = () => {
  const problems = [
    {
      icon: IndianRupee,
      title: 'Too Expensive',
      points: [
        '₹50L-2Cr for custom software',
        'Hidden costs and change requests',
        'Built for enterprises, priced for enterprises',
      ],
      color: 'text-red-500',
      bgColor: 'bg-red-50',
    },
    {
      icon: Clock,
      title: 'Too Slow',
      points: [
        '6-12 months for delivery',
        'Endless meetings and approvals',
        'Outdated development processes',
      ],
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
    },
    {
      icon: UserX,
      title: 'Poor Communication',
      points: [
        'Talk to PMs, not developers',
        'Lost in translation',
        'No direct founder access',
      ],
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section className="section-padding bg-[#f8fafc]" data-testid="problem-section">
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-[#ff6d00] mb-3 block">
            The Problem
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#212121] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="problem-headline"
          >
            Traditional Software Agencies Are Broken
          </h2>
          <p className="text-base md:text-lg text-[#5a6b8c] max-w-2xl mx-auto">
            Here's why startups and SMBs struggle with traditional development partners
          </p>
        </motion.div>

        {/* Problem Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 md:gap-8"
        >
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300"
              data-testid={`problem-card-${index}`}
            >
              <div className={`w-12 h-12 rounded-xl ${problem.bgColor} flex items-center justify-center mb-5`}>
                <problem.icon className={`w-6 h-6 ${problem.color}`} />
              </div>
              <h3
                className="text-xl md:text-2xl font-semibold text-[#212121] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {problem.title}
              </h3>
              <ul className="space-y-3">
                {problem.points.map((point, pIndex) => (
                  <li key={pIndex} className="flex items-start gap-3">
                    <span className={`w-1.5 h-1.5 rounded-full ${problem.color.replace('text-', 'bg-')} mt-2`} />
                    <span className="text-[#5a6b8c]">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Problem;
